

import firebase from 'firebase/app';
import { store } from '@/store.ts';
import * as Constants from '@/constants.ts';
import * as Util from '@/util.ts';
import * as Enums from '@/enums.ts';
import router from '@/router.ts';


/*
  When we know the user has an exchange document for this establishment then we can start watching it for updates with this function.
*/
export const onExchangeDocReady = () => {
  setUpWatchers();
  setUpNotifications();
  Util.callAfterSignInCallBack();
};

/*
  Set up push notifications. This generates a push notification token and puts it in the exchange document.
*/
const setUpNotifications = () => {

  if (!('Notification' in window)) {
    return; // Push notifications not supported in this case.
  }

  const currentUserEstablishmentExchangeDoc = store.state.currentUserEstablishmentExchangeDoc;

  if (!currentUserEstablishmentExchangeDoc) {
    Util.showError('setUpNotifications: currentUserEstablishmentExchangeDoc should exist but does not.');
    return;
  }

  if (firebase.messaging.isSupported()) {
    const messaging = firebase.messaging();
    messaging.onTokenRefresh(getToken);
    requestBrowserPermissionForNotifications();

    function getToken() {
      messaging.getToken().then((token) => {
        if (token && currentUserEstablishmentExchangeDoc) {
          currentUserEstablishmentExchangeDoc.update({
            userNotificationToken: token,
          }).catch((err) => {
            console.error('Could not store authorization token. ' + err);
          });
        } else {
          requestBrowserPermissionForNotifications();
        }
      }).catch((err) => {
        console.error('Get notification token call failed. ' + err);
      });
    }

    function requestBrowserPermissionForNotifications() {
      if (!store.state.serviceWorkerRegistation) {
        Util.nonDebugLog('Failed to register service worker.');
        return;
      }
      messaging.useServiceWorker(store.state.serviceWorkerRegistation);

      function requestNotificationsPermission() {
        messaging.requestPermission().then(() => {
          getToken(); // Attempt to get token again not that permission has been granted.
        }).catch((err) => {
          console.error('No permission given for push notifications ' + err);
        });
      }

      navigator.permissions.query({ name: 'notifications' }).then((result) => {
        switch (result.state) {
          case 'granted':
            requestNotificationsPermission();
            break;
          case 'prompt':
            Util.showConfirmation(Util.t?.requestingNotificationsModalTitle, Util.t?.requestingNotificationsModalDescription, () => {
              requestNotificationsPermission();
            });
            break;
          case 'denied':
            // Do nothing.
            break;
        }
      });
    }
  } else {
    console.error('Firebase messaging not supported.');
  }
};

/*
  This function sets up a watcher with firebase to see when the exchange document updates.
  On every update we want to show a notification to the user.
*/
const setUpWatchers = () => {

  const currentUserEstablishmentExchangeDoc = store.state.currentUserEstablishmentExchangeDoc;

  if (!currentUserEstablishmentExchangeDoc) {
    Util.showError('setUpWatchers: currentUserEstablishmentExchangeDoc should exist but does not.');
    return;
  }

  currentUserEstablishmentExchangeDoc.onSnapshot((watchedDocSnap: firebase.firestore.DocumentSnapshot) => {

    if (watchedDocSnap.exists) {

      const userEstablishmentExchangeObject: UserEstablishmentExchangeObject = watchedDocSnap.data() as UserEstablishmentExchangeObject;
      store.commit('setCurrentUserEstablishmentExchangeObject', userEstablishmentExchangeObject);

      const currNewestNotificationDateCreated = 0;
      let newestNotification: TMNotification | boolean = false;

      // Find the newest notification.
      userEstablishmentExchangeObject.notificationsToTheUser.forEach((n: TMNotification) => {
        if (n.dateCreated as number > currNewestNotificationDateCreated) {
          newestNotification = n;
        }
      });

      if (newestNotification) {
        const currNotification: TMNotification = newestNotification as TMNotification;
        if (Util.localStoreGet('idForLastShownNotification') !== currNotification.notificationId) {
          switch (currNotification.status) {
            case Enums.eNotificationStatuses.accepted:
              switch (currNotification.type) {
                case Enums.eNotificationTypes.takeoutOrder:
                case Enums.eNotificationTypes.deliveryOrder:
                case Enums.eNotificationTypes.tablesideOrder:
                  Util.showSuccess(Util.t?.orderAccepted);
                  break;
                case Enums.eNotificationTypes.cancellationRequest:
                case Enums.eNotificationTypes.request:
                  Util.showSuccess(Util.t?.requestAccepted);
                  break;
                case Enums.eNotificationTypes.question:
                  Util.showSuccess(Util.t?.answerReceived);
                  break;
              }
              break;
            case Enums.eNotificationStatuses.rejected:
              switch (currNotification.type) {
                case Enums.eNotificationTypes.takeoutOrder:
                case Enums.eNotificationTypes.deliveryOrder:
                case Enums.eNotificationTypes.tablesideOrder:
                  Util.showError(Util.t?.orderRejected);
                  break;
                case Enums.eNotificationTypes.cancellationRequest:
                case Enums.eNotificationTypes.request:
                  Util.showError(Util.t?.requestRejected);
                  break;
                case Enums.eNotificationTypes.question:
                  Util.showError(Util.t?.answerRejected);
                  break;
              }
              break;
            case Enums.eNotificationStatuses.cancelled:
              switch (currNotification.type) {
                case Enums.eNotificationTypes.tablesideOrder:
                  Util.showError(Util.t?.orderCancelled);
                  break;
              }
              break;
            case Enums.eNotificationStatuses.readyForPickUp:
              Util.showSuccess(Util.t?.yourOrderIsReadyForPickUp);
              break;
            case Enums.eNotificationStatuses.dispatched:
              Util.showSuccess(Util.t?.yourDeliveryOrderIsOnItsWay);
              break;

          }
          Util.localStoreSet('idForLastShownNotification', currNotification.notificationId);
        }
      }
    } else {
      if (store.state.appMode === Enums.eAppModes.tableside) {
        Util.rootComponent.movedLocation();
        Util.localStoreRemove('establishmentName');
        Util.localStoreRemove('stationCode');
        Util.localStoreRemove('toBeSentOrders');
        Util.localStoreRemove('lastSentOrders');
      }
      store.commit('setCurrentUserEstablishmentExchangeObject', Util.deepCopy(Constants.EMPTY_USER_ESTABLISHMENT_ECHANGE_OBJ));
      store.commit('setCurrentUserEstablishmentExchangeDoc', null);
      Util.showNotice(Util.t?.theEstablishmentClosedYouOut);
    }
  });
};

/*
  Call this whenever a request or order is made by the user.
  It will update the current UserEstablishmentExchangeDoc which is the vehicle for all communication between a user and the establishment.
  If the UserEstablishmentExchangeDoc does not exist this function creates it for the first request.
*/
export const updateExchangeObject = (update: UserEstablishmentExchangeObjectUpdate = {}, toastMessage: string = '', clearToBeSetOrders: boolean = false) => {
  Util.showLoading();

  return new Promise((resolve: any, reject: any) => {

    if (store.state.appMode === Enums.eAppModes.viewOnly) {
      // We never want to have any kind of communication with the establishment in view only mode. This ensures that if we accidently allow the user to use order/message functionality in view only mode that nothing will actually happen.
      reject('Can\'t update an exchange object in view only mode.');
      return;
    }

    if (!store.state.isLoggedIn) {
      reject('Can\'t update an exchange object if not logged in.');
      return;
    }

    // Remove the fields that we want to prevent updates for. Do not add this to the formatExchangeObjUpdateData function. This should only exist when creating a new exchange object, in which case it gets added below.
    delete update.docId;

    const currentUserEstablishmentExchangeDoc = store.state.currentUserEstablishmentExchangeDoc;

    function formatExchangeObjUpdateData(exchangeObjUpdate: UserEstablishmentExchangeObjectUpdate) {
      const stationId: number = store.state.stationId;
      if (stationId === undefined || stationId === null || stationId === -1) {
        Util.showError('No station ID provided for update or set of exchange document');
        return false;
      }
      if (store.state.currentClientUser) {
        const contactEmail: string = store.state.currentClientUser.settings.contactEmail || store.state.currentClientUser.email;
        exchangeObjUpdate.contactEmail = contactEmail;
        exchangeObjUpdate.contactNumber = store.state.currentClientUser.settings.contactNumber as number || null;
      }
      if (Constants.TAKEOUT_STATION_IDS.includes(stationId)) {
        exchangeObjUpdate.takeOutStationId = stationId;
      } else if (Constants.DELIVERY_STATION_IDS.includes(stationId)) {
        exchangeObjUpdate.deliveryStationId = stationId;
      } else {
        exchangeObjUpdate.tablesideStationId = stationId;
      }
      exchangeObjUpdate.stationId = stationId;
      exchangeObjUpdate.referenceName = store.state.referenceName || 'No Name'; // Should never be no name. If this shows up then we have an error.
      exchangeObjUpdate.dateUpdated = firebase.firestore.FieldValue.serverTimestamp();
      exchangeObjUpdate.tablesideViewedCounts = store.state.tablesideViewedCounts;
      exchangeObjUpdate.takeoutViewedCounts = store.state.takeoutViewedCounts;
      exchangeObjUpdate.deliveryViewedCounts = store.state.deliveryViewedCounts;

      // Delete the protected fields
      delete (exchangeObjUpdate as any).verifiedPayments; // This field cannot be updated by the user.
      delete (exchangeObjUpdate as any).verifiedAuthorizations; // This field cannot be updated by the user.
      delete (exchangeObjUpdate as any).verifiedCancellations; // This field cannot be updated by the user.
      return true;
    }

    function onSuccess() {
      if (toastMessage) {
        Util.showSuccess(toastMessage);
      }
      if (clearToBeSetOrders) {
        Util.setToBeSentOrders([]);
      }
      resolve();
    }

    if (currentUserEstablishmentExchangeDoc) { // If this exists then we've made orders before for this session.

      if (Object.keys(update).length === 0) {
        // Don't update if the update is just empty. Empty updates only make sense for creating the exchange object.
        resolve('The update was empty');
        Util.hideLoading();
        return;
      }

      if (!formatExchangeObjUpdateData(update)) {
        return;
      }

      currentUserEstablishmentExchangeDoc.update(update)
        .then(() => {
          onSuccess();
        })
        .catch((error: any) => {
          const errorString = 'There was a problem updating the exchange object. ' + error?.message;
          reject(errorString);
          Util.showError(errorString);
        }).finally(() => {
          Util.hideLoading();
        });
    } else { // Create new exchange doc for the very first order/request of this user's session. And put the order/request update into it.
      const locationDoc = store.state.currentLocationDoc;
      const userEstablishmentExchangeDoc: firebase.firestore.DocumentReference = locationDoc.collection('userLocationExchangeCollection').doc(store.state.userId);

      const newUserEstablishmentExchangeObject: UserEstablishmentExchangeObjectUpdate = {
        ...Constants.EMPTY_USER_ESTABLISHMENT_ECHANGE_OBJ,
        ...update,
        docId: userEstablishmentExchangeDoc.id,
        dateCreated: firebase.firestore.FieldValue.serverTimestamp(),
      };

      // Make sure there's no dot notation since we're about to do a set
      Object.keys(newUserEstablishmentExchangeObject).forEach((key: string) => {
        if (key.includes('.')) {
          Util.showError('Update keys cannot contain a dot when creating a new exchange object.');
        }
      });

      // Should only have to set this when creating a new exchange object.
      newUserEstablishmentExchangeObject.isFirstTimeAtLocation = store.state.isFirstTimeAtLocation;

      if (!formatExchangeObjUpdateData(newUserEstablishmentExchangeObject)) {
        return;
      }

      userEstablishmentExchangeDoc.set(newUserEstablishmentExchangeObject).then(() => {
        store.commit('setCurrentUserEstablishmentExchangeDoc', userEstablishmentExchangeDoc);
        store.commit('setCurrentUserEstablishmentExchangeObject', newUserEstablishmentExchangeObject);
        onSuccess();
        onExchangeDocReady();
      }).catch((error: any) => {
        const errorString = 'There was a problem creating the exchange object for this session. ' + error?.message;
        reject(errorString);
        Util.showError(errorString);
        Util.updateBannedUsers(); // Update this list in case this was the reason why.
      }).finally(() => {
        Util.hideLoading();
      });
    }
  });
};

export const deleteUserAndExchangeDoc = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const currUser = firebase.auth().currentUser;
    if (!currUser) {
      reject('Not signed in anonymously. Cannot delete the exchange object.');
      return;
    }

    const deleteUser = () => {
      currUser.delete().then(() => {
        Util.nonDebugLog(`Deleted user ${currUser.uid} and the associated exchange object if it existed.`);
        resolve(true);
      }).catch((error: any) => {
        Util.showError('There was a problem deleting the anonymous user. ' + error?.message);
        reject(error);
      });
    };

    if (store.state.currentUserEstablishmentExchangeDoc) {
      // Delete the document first otherwise we won't have permission to after we delete our anonymous user.
      return store.state.currentUserEstablishmentExchangeDoc.delete().then(() => {
        store.commit('setCurrentUserEstablishmentExchangeDoc', null);
      }).catch((error: any) => {
        Util.showError('There was a problem deleting the exchange object for the anonymous user. ' + error?.message);
        reject(error);
      }).finally(() => {
        deleteUser();
      });
    } else {
      // No exchange object in this case so we can just go straight to deleting the user, there's nothing to move over
      deleteUser();
    }
  });
};
